import type { CaptionedImage, Card } from "@braze/web-sdk";
import type { BrazeContentCard } from "@picklebet/messaging-bridge";
import { convertAESTToDateObject } from "utilities/datetime";
import { parseBool } from "utilities/helpers";
import type {
  ContentCard,
  ContentCardExtras,
  ContentCardPlacement,
} from "../types";

const placements: ContentCardPlacement[] = ["banner", "promotion"];

const parsePlacement = (value: string | undefined) =>
  placements.find((placement) => placement === value);

const parseNumber = (value: string | undefined) => {
  // Number("") is 0, which would read as a real value rather than an absent one.
  if (!value?.trim()) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseExpiryDate = (value: string | undefined) =>
  value ? convertAESTToDateObject(value) ?? undefined : undefined;

// Braze has no schema on their end, so every extras value arrives as a string a
// marketer typed into the campaign composer. Anything unrecognised parses to
// undefined and the consumer applies its own fallback.
const parseExtras = (rawExtras: Record<string, string>): ContentCardExtras => ({
  placement: parsePlacement(rawExtras.type),
  isPromotion: parseBool(rawExtras.isPromotion),
  showLoggedIn: parseBool(rawExtras.showLoggedIn),
  showLoggedOut: parseBool(rawExtras.showLoggedOut),
  racingPage: parseBool(rawExtras.racingPage),
  sportsPage: parseBool(rawExtras.sportsPage),
  esportsPage: parseBool(rawExtras.esportsPage),
  pickemsPage: parseBool(rawExtras.pickemsPage),
  casinoPage: parseBool(rawExtras.casinoPage),
  IOM: parseBool(rawExtras.IOM),
  NTRWC: parseBool(rawExtras.NTRWC),
  order: parseNumber(rawExtras.order),
  duration: parseNumber(rawExtras.duration),
  primaryBtnText: rawExtras.primaryBtnText,
  primaryBtnLink: rawExtras.primaryBtnLink,
  secondaryBtnText: rawExtras.secondaryBtnText,
  secondaryBtnLink: rawExtras.secondaryBtnLink,
  expiryDate: parseExpiryDate(rawExtras.expiryDate),
});

// The SDK hands us Card subclasses: ImageOnly, CaptionedImage and ClassicCard
// carry display fields, ControlCard carries none. Reading them structurally
// keeps this a plain data mapping, and lets tests build fixtures without
// loading the ESM-only SDK bundle.
export type WebSdkCard = Pick<
  Card,
  "id" | "isControl" | "extras" | "expiresAt"
> &
  Partial<
    Pick<
      CaptionedImage,
      "url" | "imageUrl" | "title" | "description" | "linkText"
    >
  >;

export const fromWebSdkCard = (card: WebSdkCard): ContentCard => {
  const rawExtras = card.extras ?? {};

  return {
    id: card.id,
    url: card.url,
    imageUrl: card.imageUrl,
    title: card.title,
    description: card.description,
    linkText: card.linkText,
    isControl: card.isControl,
    expiresAt: card.expiresAt ?? undefined,
    extras: parseExtras(rawExtras),
    rawExtras,
  };
};

export const fromBridgeCard = (card: BrazeContentCard): ContentCard => {
  const rawExtras = card.extras ?? {};

  return {
    id: card.id,
    url: card.url ?? undefined,
    imageUrl: card.image ?? undefined,
    title: card.title ?? undefined,
    description: card.cardDescription ?? undefined,
    // The bridge schema carries no link text, so cards delivered inside the app
    // webview have no alt/title text where browser cards do.
    linkText: undefined,
    isControl: card.isControl,
    // A negative expiry timestamp means the card never expires.
    expiresAt: card.expiresAt > 0 ? new Date(card.expiresAt * 1000) : undefined,
    extras: parseExtras(rawExtras),
    rawExtras,
  };
};
